import { PopupImageSlideshow } from './PopupImageSlideshow'
import React, { useEffect, useId, useState } from 'react'
import { getStyledTextBlockPreviewHtml } from './popup-rich-text'

function SocialPlatformIcon({
  platform,
  size,
}: {
  platform: string;
  size: number;
}) {
  const iconSize = Math.max(Math.round(size * 0.56), 14);
  const svgStyle = { display: 'block' } as const;

  if (platform === 'instagram') {
    return (
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={svgStyle}>
        <rect x="4" y="4" width="16" height="16" rx="5" stroke="currentColor" strokeWidth="1.9" />
        <circle cx="12" cy="12" r="3.6" stroke="currentColor" strokeWidth="1.9" />
        <circle cx="17.4" cy="6.7" r="1.1" fill="currentColor" />
      </svg>
    );
  }

  if (platform === 'facebook') {
    return (
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={svgStyle}>
        <path d="M13.5 21v-7.1h2.4l.36-2.8H13.5V9.32c0-.81.22-1.37 1.39-1.37H16.4V5.44c-.27-.04-1.18-.12-2.25-.12-2.23 0-3.75 1.36-3.75 3.86v1.93H8v2.8h2.4V21z" />
      </svg>
    );
  }

  if (platform === 'tiktok') {
    return (
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={svgStyle}>
        <path d="M14.8 4c.32 2.03 1.48 3.56 3.52 4v2.49a6.33 6.33 0 0 1-3.48-1.08v5.3c0 3.14-2.08 5.29-5.1 5.29-2.63 0-4.74-1.94-4.74-4.53 0-2.78 2.17-4.72 5.2-4.72.33 0 .6.02.9.09v2.62a2.8 2.8 0 0 0-.84-.12c-1.42 0-2.43.88-2.43 2.08 0 1.18.9 2 2.12 2 1.5 0 2.34-1 2.34-2.88V4z" />
      </svg>
    );
  }

  if (platform === 'x') {
    return (
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={svgStyle}>
        <path d="M18.9 4H21l-5.95 6.8L22 20h-5.48l-4.3-5.61L7.3 20H5.2l6.36-7.27L4.9 4h5.61l3.89 5.13zm-1.92 14.39h1.17L9.88 5.5H8.63z" />
      </svg>
    );
  }

  if (platform === 'youtube') {
    return (
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={svgStyle}>
        <path d="M20.2 7.2c-.2-.8-.83-1.44-1.63-1.63C17.18 5.2 12 5.2 12 5.2s-5.18 0-6.57.37c-.8.2-1.43.84-1.63 1.63C3.43 8.6 3.43 12 3.43 12s0 3.4.37 4.8c.2.8.83 1.43 1.63 1.63 1.39.37 6.57.37 6.57.37s5.18 0 6.57-.37c.8-.2 1.43-.83 1.63-1.63.37-1.4.37-4.8.37-4.8s0-3.4-.37-4.8Z" stroke="currentColor" strokeWidth="1.8" />
        <path d="M10.2 9.5 15 12l-4.8 2.5z" fill="currentColor" />
      </svg>
    );
  }

  if (platform === 'linkedin') {
    return (
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={svgStyle}>
        <path d="M6.56 8.08a1.53 1.53 0 1 1 0-3.06 1.53 1.53 0 0 1 0 3.06ZM5.2 9.6h2.73V19H5.2zm4.42 0h2.62v1.28h.04c.36-.69 1.25-1.41 2.58-1.41 2.75 0 3.26 1.81 3.26 4.16V19h-2.73v-4.78c0-1.14-.02-2.61-1.6-2.61s-1.83 1.24-1.83 2.53V19H9.62z" />
      </svg>
    );
  }

  return (
    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={svgStyle}>
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}


type Block = Record<string, any>
type Tokens = Record<string, any>
type PreviewMode = 'desktop' | 'mobile'

const MOBILE_MEDIA_QUERY = '(max-width: 767px)'

const LEGACY_MOBILE_FIELD_MAP: Record<string, string> = {
    image_url: 'mobile_image_url',
    image_size: 'mobile_image_size',
    image_position_x: 'mobile_image_position_x',
    image_position_y: 'mobile_image_position_y',
    image_repeat: 'mobile_image_repeat',
    overlay_color: 'mobile_overlay_color',
    overlay_opacity: 'mobile_overlay_opacity',
}

const hasUsableMobileValue = (key: string, value: unknown): boolean => {
    if (value === undefined || value === null) return false
    if (key === 'image_url' && typeof value === 'string') return value.trim() !== ''
    return true
}

const hasUsableLegacyValue = (value: unknown): boolean => {
    if (value === undefined || value === null) return false
    if (typeof value === 'string') return value.trim() !== ''
    return true
}

const isBlockVisible = (block: Block, previewMode: PreviewMode): boolean => {
    if (previewMode === 'mobile') return !Boolean(block.hide_on_mobile)
    return !Boolean(block.hide_on_desktop)
}

const clampNumber = (value: unknown, minimum: number, maximum: number, fallback = minimum): number => {
    const parsed = Number(value ?? fallback)
    if (!Number.isFinite(parsed)) return fallback
    return Math.max(minimum, Math.min(maximum, parsed))
}

const resolveCornerRadii = (block: Block, fallback = 0) => {
    const uniform = block.corner_radius == null ? fallback : clampNumber(block.corner_radius, 0, 64, 0)
    return {
        topLeft: block.corner_radius_tl == null ? uniform : clampNumber(block.corner_radius_tl, 0, 64, uniform),
        topRight: block.corner_radius_tr == null ? uniform : clampNumber(block.corner_radius_tr, 0, 64, uniform),
        bottomRight: block.corner_radius_br == null ? uniform : clampNumber(block.corner_radius_br, 0, 64, uniform),
        bottomLeft: block.corner_radius_bl == null ? uniform : clampNumber(block.corner_radius_bl, 0, 64, uniform),
    }
}

const resolveBorderWidths = (block: Block) => {
    const uniform = clampNumber(block.border_width, 0, 12, 0)
    return {
        top: clampNumber(block.border_top_width, 0, 12, uniform),
        right: clampNumber(block.border_right_width, 0, 12, uniform),
        bottom: clampNumber(block.border_bottom_width, 0, 12, uniform),
        left: clampNumber(block.border_left_width, 0, 12, uniform),
    }
}

const getAlignedMaxWidthStyle = (maxWidth: number | null | undefined, align?: 'left' | 'center' | 'right'): React.CSSProperties | undefined => {
    if (!maxWidth || maxWidth <= 0) return undefined

    return {
        width: '100%',
        maxWidth: `${maxWidth}px`,
        marginLeft: align === 'right' ? 'auto' : align === 'center' || !align ? 'auto' : 0,
        marginRight: align === 'left' ? 'auto' : align === 'center' || !align ? 'auto' : 0,
    }
}

const resolveJustifyContent = (value: unknown): React.CSSProperties['justifyContent'] => {
    if (value === 'between') return 'space-between'
    if (value === 'center') return 'center'
    if (value === 'end') return 'flex-end'
    return 'flex-start'
}

const resolveAlignItems = (value: unknown): React.CSSProperties['alignItems'] => {
    if (value === 'center') return 'center'
    if (value === 'end') return 'flex-end'
    if (value === 'start') return 'flex-start'
    return 'stretch'
}

const resolveResponsiveBlock = (block: Block, previewMode: PreviewMode): Block => {
    if (previewMode === 'desktop') return block

    const resolved: Block = { ...block }
    const overrides = block.mobile_overrides && typeof block.mobile_overrides === 'object'
        ? block.mobile_overrides as Record<string, unknown>
        : {}

    Object.entries(overrides).forEach(([key, value]) => {
        if (hasUsableMobileValue(key, value)) {
            resolved[key] = value
        }
    })

    Object.entries(LEGACY_MOBILE_FIELD_MAP).forEach(([baseKey, legacyKey]) => {
        if (!Object.prototype.hasOwnProperty.call(overrides, baseKey) && hasUsableLegacyValue(block[legacyKey])) {
            resolved[baseKey] = block[legacyKey]
        }
    })

    return resolved
}

export function usePopupPreviewMode(): PreviewMode {
    const getMode = () => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'desktop'
        return window.matchMedia(MOBILE_MEDIA_QUERY).matches ? 'mobile' : 'desktop'
    }

    const [previewMode, setPreviewMode] = useState<PreviewMode>(getMode)

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined

        const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY)
        const handleChange = () => setPreviewMode(mediaQuery.matches ? 'mobile' : 'desktop')

        handleChange()
        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', handleChange)
            return () => mediaQuery.removeEventListener('change', handleChange)
        }

        mediaQuery.addListener(handleChange)
        return () => mediaQuery.removeListener(handleChange)
    }, [])

    return previewMode
}

interface EmailFieldProps {
    block: Block
    tokens: Tokens
    email: string
    isSubmitting: boolean
    isSuccess: boolean
    errorMessage: string | null
    onEmailChange: (v: string) => void
    onSubmit: (e: React.FormEvent) => void
}

function EmailFieldBlock({ block, tokens, email, isSubmitting, isSuccess, errorMessage, onEmailChange, onSubmit }: EmailFieldProps) {
    const fieldId = useId()
    const btnBg = block.background_color || tokens.accentColor || '#171717'
    const btnText = block.text_color || tokens.buttonTextColor || '#ffffff'
    const btnRadius = block.button_corner_radius ?? 14
    const btnLabel = block.label ?? 'Continue'
    const placeholder = block.placeholder ?? 'Your email'
    const successMsg = block.success_message ?? 'Check your inbox for the next step.'
    const fieldLabel = block.field_label ?? 'Email'
    const showFieldLabel = block.show_field_label !== false && String(fieldLabel).trim() !== ''
    const bodyFontFamily = tokens.bodyFontFamily || 'inherit'
    const buttonFontFamily = block.button_font_family || tokens.buttonFontFamily || bodyFontFamily
    const fieldLabelFontFamily = block.field_label_font_family || bodyFontFamily
    const inputFontFamily = block.input_font_family || bodyFontFamily
    const successFontFamily = block.success_font_family || bodyFontFamily
    const inputClassName = `popup-email-input-${String(block.id || 'field').replace(/[^a-zA-Z0-9_-]/g, '-')}`

    if (isSuccess) {
        if (!successMsg) return null
        return (
            <p role="status" aria-live="polite"
                style={{
                    textAlign: 'center',
                    fontSize: block.success_font_size ?? 14,
                    color: block.success_text_color || '#6b7280',
                    margin: 0,
                    fontFamily: successFontFamily,
                }}
            >
                {successMsg}
            </p>
        )
    }

    return (
        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: block.gap ?? 8 }}>
            <style>
                {`.${inputClassName}::placeholder{color:${block.input_placeholder_color || '#9ca3af'};opacity:1;}`}
            </style>
            <div>
                {showFieldLabel ? (
                    <label htmlFor={fieldId}
                        style={{
                            display: 'block',
                            fontSize: block.field_label_font_size ?? 12,
                            fontWeight: 500,
                            color: block.field_label_color || '#6b7280',
                            marginBottom: 4,
                            fontFamily: fieldLabelFontFamily,
                        }}
                    >
                        {fieldLabel}
                    </label>
                ) : null}
                <input
                    className={inputClassName}
                    id={fieldId}
                    aria-label={showFieldLabel ? undefined : (String(fieldLabel || '').trim() || 'Email')}
                    aria-invalid={Boolean(errorMessage)}
                    aria-describedby={errorMessage ? `${fieldId}-error` : undefined}
                    autoComplete="email"
                    required
                    type="email"
                    value={email}
                    onChange={(e) => onEmailChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={isSubmitting}
                    style={{
                        width: '100%',
                        height: 44,
                        border: `1px solid ${block.input_border_color || '#e5e7eb'}`,
                        borderRadius: block.input_corner_radius ?? 12,
                        padding: '0 12px',
                        fontSize: block.input_font_size ?? 14,
                        color: block.input_text_color || '#111827',
                        backgroundColor: block.input_background_color || '#f9fafb',
                        outline: 'none',
                        boxSizing: 'border-box',
                        fontFamily: inputFontFamily,
                    }}
                />
            </div>
            {errorMessage && (
                <p id={`${fieldId}-error`} role="alert" style={{ margin: 0, fontSize: 13, color: '#dc2626', fontFamily: bodyFontFamily }}>{errorMessage}</p>
            )}
            <button
                type="submit"
                disabled={isSubmitting}
                style={{
                    height: 44,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: btnBg,
                    color: btnText,
                    borderRadius: `${btnRadius}px`,
                    fontWeight: 600,
                    fontSize: block.button_font_size ?? 15,
                    fontFamily: buttonFontFamily,
                    border: 'none',
                    padding: 0,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.7 : 1,
                }}
            >
                {isSubmitting ? '…' : btnLabel}
            </button>
        </form>
    )
}

interface BlockProps {
    block: Block
    tokens: Tokens
    formProps: Omit<EmailFieldProps, 'block' | 'tokens'>
    previewMode: PreviewMode
    fillHeight?: boolean
    renderBlockFrame?: (block: Block, content: React.ReactNode) => React.ReactNode
}

export function readPopupAspectRatio(value: unknown): string {
    const parts=String(value ?? '').trim().split(/[:/]/).map(part=>Number(part.trim()))
    if(parts.length===1) parts.push(1)
    return parts.length===2 && parts.every(part=>Number.isFinite(part) && part>0) ? `${parts[0]} / ${parts[1]}` : '16 / 9'
}

export function popupHasFillHeight(template: {content_blocks?: Block[]}, mode: PreviewMode): boolean {
    return (template.content_blocks || []).some(block => isBlockVisible(block,mode) && block.type === 'section' && resolveResponsiveBlock(block,mode).position_mode !== 'absolute' && resolveResponsiveBlock(block,mode).height_mode === 'fill')
}

export function sortPopupBlocks(blocks: Block[], mode: PreviewMode): Block[] {
    return blocks.map((block, index) => ({block, index, order: Number(resolveResponsiveBlock(block, mode).order) || 0}))
        .sort((a,b) => a.order - b.order || a.index - b.index).map(item => item.block)
}

function renderBlockContent(rawBlock: Block, tokens: Tokens, formProps: BlockProps['formProps'], previewMode: PreviewMode, fillHeight = false, renderBlockFrame?: BlockProps['renderBlockFrame'], parentFixedHeight = false): React.ReactNode {
    if (!isBlockVisible(rawBlock, previewMode)) return null

    const block = resolveResponsiveBlock(rawBlock, previewMode)
    if (!block || !block.type) return null

    const pt = block.padding_top ?? block.padding_y ?? tokens.blockPaddingY ?? 0
    const pr = block.padding_right ?? block.padding_x ?? tokens.blockPaddingX ?? 0
    const pb = block.padding_bottom ?? block.padding_y ?? tokens.blockPaddingY ?? 0
    const pl = block.padding_left ?? block.padding_x ?? tokens.blockPaddingX ?? 0

    if (block.type === 'section') {
        const stretch = block.height_mode === 'fill'
        const ratio = block.height_mode === 'ratio'
        const fixed = block.height_mode === 'fixed' || ratio
        const children: Block[] = Array.isArray(block.children) ? block.children : []
        const backgroundImageUrl = block.image_url || ''
        const overlayColor = block.overlay_color || ''
        const overlayOpacity = clampNumber(block.overlay_opacity, 0, 100, 0) / 100
        const cornerRadii = resolveCornerRadii(block)
        const borderWidths = resolveBorderWidths(block)
        const widthMode = block.position_mode === 'absolute' ? 'full' : block.width_mode ?? (block.full_width === false ? 'constrained' : 'full')
        const outerMaxWidth = widthMode === 'constrained' ? Math.max(Number(block.max_width || 0), 0) : null
        const sectionOuterStyle = getAlignedMaxWidthStyle(outerMaxWidth, block.h_align || 'center') || { width: '100%' }
        const contentStyle = getAlignedMaxWidthStyle(
            Math.max(Number(block.content_max_width || 0), 0) || null,
            block.content_h_align || 'center'
        ) || { width: '100%' }
        const sectionStyle: React.CSSProperties = {
            ...sectionOuterStyle,
            position: 'relative',
            backgroundColor: block.background_color || undefined,
            backgroundImage: backgroundImageUrl ? `url("${backgroundImageUrl}")` : undefined,
            backgroundPosition: backgroundImageUrl ? `${block.image_position_x || 'center'} ${block.image_position_y || 'center'}` : undefined,
            backgroundRepeat: backgroundImageUrl ? block.image_repeat || 'no-repeat' : undefined,
            backgroundSize: backgroundImageUrl ? block.image_size || 'cover' : undefined,
            borderTopLeftRadius: `${cornerRadii.topLeft}px`,
            borderTopRightRadius: `${cornerRadii.topRight}px`,
            borderBottomRightRadius: `${cornerRadii.bottomRight}px`,
            borderBottomLeftRadius: `${cornerRadii.bottomLeft}px`,
            borderStyle: borderWidths.top || borderWidths.right || borderWidths.bottom || borderWidths.left ? 'solid' : undefined,
            borderColor: block.border_color || undefined,
            borderTopWidth: borderWidths.top,
            borderRightWidth: borderWidths.right,
            borderBottomWidth: borderWidths.bottom,
            borderLeftWidth: borderWidths.left,
            overflow: backgroundImageUrl || cornerRadii.topLeft || cornerRadii.topRight || cornerRadii.bottomRight || cornerRadii.bottomLeft ? 'hidden' : undefined,
            height: fillHeight ? '100%' : undefined,
            minHeight: fillHeight ? '100%' : block.min_height || undefined,
            flex: fillHeight || stretch ? '1 0 auto' : undefined,
            ...(stretch ? {display:'flex',flexDirection:'column',minHeight:block.min_height || 0} : {}),
            ...(ratio ? {position:'absolute',inset:0} : {}),
            ...(fixed ? {height:'100%',maxHeight:'100%',minHeight:0,boxSizing:'border-box',overflowY:'auto',display:'flex',flexDirection:'column'} : {}),
            boxShadow: block.shadow === 'medium' ? '0 14px 34px rgba(15,23,42,0.12)' : block.shadow === 'soft' ? '0 8px 24px rgba(15,23,42,0.08)' : undefined,
        }
        if(block.overflow_mode==='visible' || block.overflow_mode==='clip') {
            const overflow=block.overflow_mode==='visible'?'visible':'hidden'
            Object.assign(sectionStyle,{overflow,overflowX:overflow,overflowY:overflow})
        }
        const contentInnerStyle: React.CSSProperties = {
            display: 'flex',
            flexDirection: block.direction || 'column',
            gap: block.gap ?? 0,
            justifyContent: fillHeight && (!block.justify_content || block.justify_content === 'start')
                ? 'center'
                : resolveJustifyContent(block.justify_content),
            alignItems: resolveAlignItems(block.align_items),
            paddingTop: pt,
            paddingRight: pr,
            paddingBottom: pb,
            paddingLeft: pl,
            height: fillHeight ? '100%' : undefined,
            minHeight: fillHeight ? '100%' : block.min_height || undefined,
            ...(stretch ? {flex:'1 0 auto',boxSizing:'border-box'} : {}),
            ...(fixed ? {height:'100%',minHeight:0,boxSizing:'border-box',flex:'1 0 100%'} : {}),
        }
        return (
            <div
                key={block.id}
                style={sectionStyle}
            >
                {overlayColor && overlayOpacity > 0 ? (
                    <div
                        aria-hidden="true"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: overlayColor,
                            opacity: overlayOpacity,
                            pointerEvents: 'none',
                        }}
                    />
                ) : null}
                <div style={{ position: 'relative', minWidth: 0, ...contentStyle, ...(fixed ? {height:'100%',minHeight:0,boxSizing:'border-box'} : {}), ...(stretch ? {display:'flex',flexDirection:'column',flex:'1 0 auto',minHeight:0} : {}), ...(fillHeight ? { height: '100%', minHeight: '100%' } : {}) }}>
                    <div style={{ position: 'relative', minWidth: 0, ...contentInnerStyle }}>
                        {sortPopupBlocks(children, previewMode).filter(child => isBlockVisible(child, previewMode)).map((child) => renderBlock(child, tokens, formProps, previewMode, false, renderBlockFrame, block.direction || 'column', false, fixed || (stretch && parentFixedHeight)))}
                    </div>
                </div>
            </div>
        )
    }

    if (block.type === 'text' || block.type === 'unsubscribe_text') {
        // Replace all <p> tags with margin:0 — block-level properties on the wrapper div are the source of truth.
        const textHtml = getStyledTextBlockPreviewHtml(block.text || '', { align: block.text_align || 'left', fontFamily: block.font_family || tokens.bodyFontFamily, fontSize: Math.max(block.font_size ?? 16, 10), lineHeight: block.line_height ?? 1.7, compact: true })
        return (
            <div
                key={block.id}
                style={{
                    paddingTop: pt,
                    paddingRight: pr,
                    paddingBottom: pb,
                    paddingLeft: pl,
                    fontFamily: block.font_family || tokens.bodyFontFamily || 'inherit',
                    fontSize: `${Math.max(block.font_size ?? 16, 10)}px`,
                    lineHeight: block.line_height ?? 1.7,
                    color: block.text_color || tokens.textColor || 'inherit',
                    textAlign: (block.text_align as any) || 'left',
                    wordBreak: 'break-word',
                }}
                dangerouslySetInnerHTML={{ __html: textHtml }}
            />
        )
    }

    if (block.type === 'image') {
        const url = block.image_url || ''
        const slides=block.image_mode==='slideshow' ? [...(url?[{id:'primary',image_url:url,alt:block.alt??block.label??''}]:[]),...(Array.isArray(block.image_slides)?block.image_slides:[])].filter(slide=>slide && typeof slide.image_url==='string' && slide.image_url.trim()) : []
        if (!url && !slides.length) return null
        const align = block.text_align || 'left'
        const maxW = block.position_mode === 'absolute' ? '100%' : block.max_width ? `min(100%, ${block.max_width}px)` : '100%'
        const filling = block.image_layout === 'fill' || block.height_mode === 'fixed' || block.height_mode === 'ratio'
        const imageStyle: React.CSSProperties = {
            display: filling ? 'block' : 'inline-block', verticalAlign:'middle', width: filling ? '100%' : maxW,
            height: filling ? '100%' : 'auto', objectFit: filling ? block.image_fit || 'cover' : undefined,
            objectPosition: `${block.image_position_x || 'center'} ${block.image_position_y || 'center'}`,
            borderTopLeftRadius:resolveCornerRadii(block).topLeft, borderTopRightRadius:resolveCornerRadii(block).topRight,
            borderBottomRightRadius:resolveCornerRadii(block).bottomRight, borderBottomLeftRadius:resolveCornerRadii(block).bottomLeft,
        }
        const image = <img src={url} alt={block.alt ?? block.label ?? ''} style={imageStyle} />
        return <div style={{paddingTop:pt,paddingRight:pr,paddingBottom:pb,paddingLeft:pl,textAlign:align as any,
            ...(filling ? {position:'absolute',inset:0,boxSizing:'border-box'} : {})}}>
            {slides.length ? <PopupImageSlideshow slides={slides} imageStyle={imageStyle} href={block.url} interval={block.slideshow_interval_ms} transition={block.slideshow_transition_ms??350} autoplay={block.slideshow_autoplay!==false} controls={block.slideshow_controls===true} showPlayPause={block.slideshow_show_play_pause!==false} /> : block.url ? <a href={block.url} style={{display:filling?'block':'inline-block',height:filling?'100%':undefined,lineHeight:0,textDecoration:'none'}}>{image}</a> : image}
        </div>
    }

    if (block.type === 'button') {
        const bg = block.background_color || tokens.accentColor || '#171717'
        const color = block.text_color || tokens.buttonTextColor || '#ffffff'
        const radius = block.button_corner_radius ?? block.corner_radius ?? tokens.buttonRadius ?? 20
        const padX = Math.max(block.button_padding_x ?? tokens.buttonPaddingX ?? 20, 8)
        const padY = Math.max(block.button_padding_y ?? tokens.buttonPaddingY ?? 12, 8)
        const align = block.text_align || 'center'
        return (
            <div key={block.id} style={{ paddingTop: pt, paddingRight: pr, paddingBottom: pb, paddingLeft: pl, textAlign: align as any }}>
                <a
                    href={block.url || '#'}
                    style={{
                        display: block.button_full_width ? 'block' : 'inline-block',
                        backgroundColor: bg,
                        color,
                        borderRadius: `${radius}px`,
                        padding: `${padY}px ${padX}px`,
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: `${Math.max(block.font_size ?? 15, 10)}px`,
                        lineHeight: block.line_height ?? 1.1,
                        border: `1px solid ${bg}`,
                        boxSizing: 'border-box',
                        textAlign: 'center',
                        wordBreak: 'break-word',
                        fontFamily: block.font_family || tokens.buttonFontFamily || tokens.bodyFontFamily || 'inherit',
                    }}
                >
                    {block.label || 'Button'}
                </a>
            </div>
        )
    }

    if (block.type === 'social_links') {
        const align = block.text_align || 'center'
        const size = Math.max(Number(block.height || 38), 20)
        const links = Array.isArray(block.social_links) ? block.social_links.filter((item: any) => item?.url) : []
        return <div style={{paddingTop:pt,paddingRight:pr,paddingBottom:pb,paddingLeft:pl,textAlign:align}}>
            <div style={{display:'inline-flex',flexWrap:'wrap',gap:Math.max(Number(block.gap || 12),0),justifyContent:align==='left'?'flex-start':align==='right'?'flex-end':'center'}}>
                {links.map((item: any,index: number)=><a key={`${item.platform}-${index}`} href={item.url} aria-label={item.platform} style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:size,minWidth:size,height:size,borderRadius:block.corner_radius ?? 999,background:block.background_color || 'transparent',color:block.text_color || tokens.accentColor || '#6f4ef2',textDecoration:'none',lineHeight:1}}><SocialPlatformIcon platform={item.platform} size={size} /></a>)}
            </div>
        </div>
    }

    if (block.type === 'divider') {
        return (
            <div key={block.id} style={{ paddingTop: pt, paddingRight: pr, paddingBottom: pb, paddingLeft: pl }}>
                <hr style={{ border: 'none', borderTop: `${block.border_width ?? 1}px solid ${block.border_color || '#e5e7eb'}`, margin: 0 }} />
            </div>
        )
    }

    if (block.type === 'spacer') {
        return <div key={block.id} style={{ height: block.height ?? 24 }} />
    }

    if (block.type === 'email_field') {
        return (
            <div key={block.id} style={{ paddingTop: pt, paddingRight: pr, paddingBottom: pb, paddingLeft: pl, backgroundColor: block.container_background_color || undefined }}>
                <EmailFieldBlock block={block} tokens={tokens} {...formProps} />
            </div>
        )
    }

    return null
}

function renderBlock(rawBlock: Block, tokens: Tokens, formProps: BlockProps['formProps'], previewMode: PreviewMode, fillHeight = false, decorate?: BlockProps['renderBlockFrame'], parentDirection?: string, isLast = false, parentFixedHeight = false): React.ReactNode {
    if (!isBlockVisible(rawBlock, previewMode)) return null
    const block = resolveResponsiveBlock(rawBlock, previewMode)
    const legacyFill = fillHeight && block.height_mode == null
    const content = renderBlockContent(rawBlock, tokens, formProps, previewMode, legacyFill, decorate, parentFixedHeight)
    if (!content) return decorate ? decorate(rawBlock, null) : null
    const section = block.type === 'section'
    const constrained = (block.width_mode ?? (block.full_width === false ? 'constrained' : 'full')) === 'constrained'
    const frameStyle: React.CSSProperties = {
        position: 'relative', minWidth: 0, boxSizing: 'border-box',
        marginTop: block.margin_top ?? (parentDirection || section ? 0 : tokens.blockMarginTop ?? 0),
        marginBottom: block.margin_bottom ?? (parentDirection || section || isLast ? 0 : tokens.blockMarginBottom ?? tokens.blockSpacing ?? 20),
        marginLeft: block.margin_left ?? 0, marginRight: block.margin_right ?? 0,
        height: legacyFill ? '100%' : undefined,
        ...(section && parentDirection === 'row' ? constrained ? {flex:'0 0 auto',width:block.max_width || 'auto',maxWidth:block.max_width || undefined} : {flex:'1 1 0%',width:'100%'} : {}),
    }
    if (section && block.height_mode === 'fill') {
        Object.assign(frameStyle,{display:'flex',flexDirection:'column',minHeight:block.min_height || 0,alignSelf:'stretch', ...(parentDirection === 'row' ? {} : {flex:'1 0 auto'})})
    }
    if (block.type === 'image' && (block.image_layout === 'fill' || block.height_mode === 'fixed' || block.height_mode === 'ratio')) {
        Object.assign(frameStyle, {flex:'1 1 0%', alignSelf:'stretch', minHeight:parentFixedHeight ? 0 : Math.max(Number(block.min_height) || 240, 1), height:fillHeight?'100%':undefined, overflow:'hidden'})
    }
    if ((section || block.type === 'image') && block.height_mode === 'fixed') {
        Object.assign(frameStyle,{height:clampNumber(block.height,40,2000,240),minHeight:0,maxHeight:clampNumber(block.height,40,2000,240),flex:parentDirection==='row' ? frameStyle.flex || '0 0 auto' : '0 0 auto'})
    }
    if ((section || block.type === 'image') && block.height_mode === 'ratio') {
        Object.assign(frameStyle,{width:'100%',height:'auto',maxHeight:undefined,minHeight:0,aspectRatio:readPopupAspectRatio(block.aspect_ratio),alignSelf:'flex-start',flex:parentDirection==='row' ? frameStyle.flex || '0 0 auto' : '0 0 auto'})
    }
    if (!section) {
        const radii = resolveCornerRadii(block, block.type === 'image' ? 0 : clampNumber(tokens.blockRadius, 0, 128, 24))
        const borders = resolveBorderWidths(block)
        Object.assign(frameStyle, {backgroundColor:block.module_background_color || undefined, borderTopLeftRadius:radii.topLeft,borderTopRightRadius:radii.topRight,borderBottomRightRadius:radii.bottomRight,borderBottomLeftRadius:radii.bottomLeft,borderStyle:'solid',borderColor:block.border_color || undefined,borderTopWidth:borders.top,borderRightWidth:borders.right,borderBottomWidth:borders.bottom,borderLeftWidth:borders.left})
    }
    if(block.z_index != null || block.position_mode === 'absolute') frameStyle.zIndex=clampNumber(block.z_index,0,100,1)
    if(block.position_mode === 'relative') Object.assign(frameStyle,{left:clampNumber(block.position_x,-1000,1000,0),top:clampNumber(block.position_y,-1000,1000,0)})
    if(block.position_mode === 'absolute') {
        const anchors=['top_left','top_center','top_right','center_left','center','center_right','bottom_left','bottom_center','bottom_right']
        const anchor=anchors.includes(block.position_anchor) ? block.position_anchor : 'top_left'
        const x=clampNumber(block.position_x,-1000,1000,0), y=clampNumber(block.position_y,-1000,1000,0)
        const horizontal=anchor==='center' ? 'center' : anchor.split('_')[1]
        const vertical=anchor==='center' ? 'center' : anchor.split('_')[0]
        const centered=(offset:number)=>`calc(50% ${offset>=0?'+':'-'} ${Math.abs(offset)}px)`
        Object.assign(frameStyle,{position:'absolute',flex:'none',alignSelf:'auto',maxWidth:undefined,...(block.height_mode==='fill'?{height:'100%'}:{}),width:`${clampNumber(block.position_width,1,block.position_width_unit==='px'?2000:200,block.position_width_unit==='px'?160:35)}${block.position_width_unit==='px'?'px':'%'}`,
            ...(horizontal==='right' ? {right:x} : {left:horizontal==='center'?centered(x):x}),
            ...(vertical==='bottom' ? {bottom:y} : {top:vertical==='center'?centered(y):y}),
            transform:horizontal==='center' && vertical==='center'?'translate(-50%, -50%)':horizontal==='center'?'translateX(-50%)':vertical==='center'?'translateY(-50%)':undefined,
        })
    }
    const frame = <div key={rawBlock.id} data-popup-block-id={rawBlock.id} style={frameStyle}>{content}</div>
    return decorate ? decorate(rawBlock, frame) : frame
}

interface PopupBlockRendererProps {
    template: { content_blocks: Block[]; design_tokens?: Tokens }
    formProps: BlockProps['formProps']
    fillHeight?: boolean
    previewMode?: PreviewMode
    renderBlockFrame?: BlockProps['renderBlockFrame']
}

export function PopupBlockRenderer({ template, formProps, fillHeight = false, previewMode: explicitMode, renderBlockFrame }: PopupBlockRendererProps) {
    const tokens: Tokens = template.design_tokens || {}
    const blocks: Block[] = Array.isArray(template.content_blocks) ? template.content_blocks : []
    const detectedMode = usePopupPreviewMode()
    const previewMode = explicitMode || detectedMode
    const contentFillHeight = fillHeight || popupHasFillHeight(template, previewMode)
    const visibleBlocks = sortPopupBlocks(blocks, previewMode).filter(block => isBlockVisible(block, previewMode))
    const flowBlocks = visibleBlocks.filter(block=>resolveResponsiveBlock(block,previewMode).position_mode!=='absolute')

    return (
        <div className="kibu-popup-content" style={{ fontFamily: tokens.bodyFontFamily || 'Arial, Helvetica, sans-serif', fontSize: 16, lineHeight: 1.5, color: tokens.textColor || '#111827', position:'relative',isolation:'isolate',width: '100%', height: contentFillHeight ? '100%' : undefined, display: contentFillHeight ? 'flex' : undefined, flexDirection: contentFillHeight ? 'column' : undefined }}>
            {visibleBlocks.map((block, index) => renderBlock(block, tokens, formProps, previewMode, fillHeight && flowBlocks.length === 1 && flowBlocks[0].id===block.id && block.type === 'section', renderBlockFrame, undefined, block.id===flowBlocks[flowBlocks.length-1]?.id))}
        </div>
    )
}
